/**
 * Valódi backend kliens — pontosan a `MockApi` signature-jét implementálja.
 * A `VITE_API_BASE_URL` env változó alapján a `mockClient` ezt használja
 * minden helyen ahol a UI az `api`-t hívja.
 *
 * JWT autentikáció: a `Bearer` token a `localStorage.adaptivo.szamla.token`-ban,
 * a session cookie HttpOnly is el van helyezve (defense in depth).
 */
import type {
  AccountantExport,
  AiAccountantPrep,
  AiCashflow,
  AiMonthlySummary,
  AiSuggestion,
  AuditLogEntry,
  AuthSession,
  BankTransaction,
  BankingImportResponse,
  BreakdownResponse,
  BreakdownTrendResponse,
  CostAllocationPayload,
  CronJobStatus,
  CronRun,
  HealthIntegrationsResponse,
  InvoiceSplit,
  Company,
  CompanySettings,
  CreateUserPayload,
  DiscrepancyIssue,
  DiscrepancyType,
  EmailAccountSettings,
  EmailInboxItem,
  ExtractResponse,
  ImapFoldersResult,
  InvoiceComment,
  InvoiceDetail,
  InvoiceListItem,
  JobResponse,
  JobRun,
  LinkDownloadResponse,
  ListInvoicesParams,
  NavCredentials,
  Notification,
  OutgoingEmailSettings,
  PaginatedResponse,
  RecordPaymentPayload,
  RecordPaymentResponse,
  Role,
  SzamlazzCredentialsStatus,
  UpdateCompanySettingsPayload,
  UpdateSzamlazzPayload,
  UpdateEmailAccountPayload,
  UpdateNavCredentialsPayload,
  UpdateOutgoingEmailPayload,
  UploadInvoicePayload,
  UploadInvoiceResponse,
  UpsertCompanyPayload,
  User,
  UserListItem,
  VatMonthlySummary,
  WorkflowStatus,
} from './types'

// VITE_API_BASE_URL:
//   "" (üres string)            → relatív URL-eket használunk (nginx proxy oldja meg /api/-t)
//   "https://api.example.hu"    → abszolút prefix
//   undefined (nem set-elt)     → mock backend (fixtures + localStorage)
const RAW_BASE = import.meta.env.VITE_API_BASE_URL as string | undefined
const BASE = (RAW_BASE ?? '').replace(/\/+$/, '')

const TOKEN_KEY = 'adaptivo.szamla.token'

function getToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setStoredToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token)
    else window.localStorage.removeItem(TOKEN_KEY)
  } catch {
    // noop
  }
}

function activeCompanyHeader(): string | undefined {
  try {
    return window.localStorage.getItem('adaptivo.szamla.activeCompany') ?? undefined
  } catch {
    return undefined
  }
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  asBytes?: boolean
  noAuth?: boolean
  formData?: FormData
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {}
  if (!opts.formData) headers['Content-Type'] = 'application/json'
  headers.Accept = opts.asBytes ? 'application/pdf' : 'application/json'
  if (!opts.noAuth) {
    const t = getToken()
    if (t) headers.Authorization = `Bearer ${t}`
    const co = activeCompanyHeader()
    if (co) headers['X-Active-Company'] = co
  }
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
  }
  if (opts.formData) init.body = opts.formData
  else if (opts.body !== undefined) init.body = JSON.stringify(opts.body)

  const url = BASE + path
  let res: Response
  try {
    res = await fetch(url, init)
  } catch (err) {
    throw new ApiError(0, 'network', err instanceof Error ? err.message : 'Hálózati hiba')
  }
  if (opts.asBytes) {
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new ApiError(res.status, 'fetch_failed', text || res.statusText)
    }
    const buf = await res.arrayBuffer()
    return new Uint8Array(buf) as unknown as T
  }

  const text = await res.text()
  let parsed: unknown = null
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = { detail: text }
    }
  }
  if (!res.ok) {
    const p = parsed as { code?: string; detail?: string; title?: string }
    const code = p?.code ?? p?.title ?? `http_${res.status}`
    const detail = p?.detail ?? res.statusText
    throw new ApiError(res.status, code, detail)
  }
  return parsed as T
}

export class ApiError extends Error {
  status: number
  code: string
  constructor(status: number, code: string, detail: string) {
    super(detail)
    this.status = status
    this.code = code
  }
}

function qs(params: Record<string, unknown>): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue
    if (Array.isArray(v)) {
      for (const item of v) parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(item))}`)
    } else {
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    }
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

interface AuthResponseShape {
  token: string
  user: User
  tenantId: string
  memberships: AuthSession['memberships']
  expiresAt: string
}

function toSession(r: AuthResponseShape): AuthSession {
  setStoredToken(r.token)
  return {
    user: r.user,
    tenantId: r.tenantId,
    memberships: r.memberships,
    expiresAt: r.expiresAt,
  }
}

export const fetchApi = {
  async needsSetup(): Promise<boolean> {
    const r = await request<{ needsSetup: boolean }>('/api/v1/auth/needs-setup', { noAuth: true })
    return r.needsSetup
  },

  async setupInitialAdmin(payload: {
    fullName: string
    email: string
    password: string
    companyLegalName: string
    companyTaxNumber: string
    companyCountryCode?: string
    companyCurrency?: string
  }): Promise<AuthSession> {
    const r = await request<AuthResponseShape>('/api/v1/auth/setup', {
      method: 'POST',
      body: payload,
      noAuth: true,
    })
    return toSession(r)
  },

  async login(email: string, password: string): Promise<AuthSession> {
    const r = await request<AuthResponseShape>('/api/v1/auth/login', {
      method: 'POST',
      body: { email, password },
      noAuth: true,
    })
    return toSession(r)
  },

  async logout(): Promise<void> {
    try {
      await request('/api/v1/auth/logout', { method: 'POST' })
    } finally {
      setStoredToken(null)
    }
  },

  async me(): Promise<AuthSession> {
    return request<AuthSession>('/api/v1/auth/me')
  },

  /** Meghívó token validáció — ha érvényes, visszaadja az emailt és nevet. */
  async validateInviteToken(token: string): Promise<{ email: string; fullName: string }> {
    return request<{ email: string; fullName: string }>(
      `/api/v1/users/accept-invite/${encodeURIComponent(token)}`,
      { noAuth: true },
    )
  },

  async acceptInvite(token: string, password: string): Promise<AuthSession> {
    const r = await request<AuthResponseShape>('/api/v1/users/accept-invite', {
      method: 'POST',
      body: { token, password },
      noAuth: true,
    })
    return toSession(r)
  },

  async listUsers(): Promise<UserListItem[]> {
    return request<UserListItem[]>('/api/v1/users')
  },

  async createUser(payload: CreateUserPayload): Promise<UserListItem & { inviteSent?: boolean; inviteSendError?: string | null; inviteSkipReason?: string | null }> {
    return request('/api/v1/users', { method: 'POST', body: payload })
  },

  async resendInvite(userId: string): Promise<void> {
    await request(`/api/v1/users/resend-invite/${encodeURIComponent(userId)}`, { method: 'POST' })
  },

  async listCompanies(): Promise<Company[]> {
    return request<Company[]>('/api/v1/companies')
  },

  async upsertCompany(payload: UpsertCompanyPayload): Promise<Company> {
    return request('/api/v1/companies', { method: 'POST', body: payload })
  },

  async listAuditLogs(opts?: { limit?: number; subjectType?: string }): Promise<AuditLogEntry[]> {
    return request<AuditLogEntry[]>(
      `/api/v1/audit-logs${qs({ subjectType: opts?.subjectType, limit: opts?.limit })}`,
    )
  },

  async listInvoices(params: ListInvoicesParams): Promise<PaginatedResponse<InvoiceListItem>> {
    return request<PaginatedResponse<InvoiceListItem>>(
      `/api/v1/invoices${qs({
        companyId: params.companyId,
        from: params.from,
        to: params.to,
        projectCode: params.projectCode,
        paidBy: params.paidBy,
        splitUserId: params.splitUserId,
        q: params.q,
        page: params.page,
        pageSize: params.pageSize,
      })}`,
    )
  },

  async getInvoice(id: string): Promise<InvoiceDetail> {
    return request<InvoiceDetail>(`/api/v1/invoices/${encodeURIComponent(id)}`)
  },

  async getInvoicePdf(id: string): Promise<Uint8Array> {
    return request<Uint8Array>(`/api/v1/invoices/${encodeURIComponent(id)}/pdf`, { asBytes: true })
  },

  async updateInvoiceStatus(id: string, toStatus: WorkflowStatus, reason?: string): Promise<InvoiceListItem> {
    await request(`/api/v1/invoices/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: { toStatus, reason },
    })
    return this.getInvoice(id) as unknown as InvoiceListItem
  },

  async approveInvoice(id: string, comment?: string): Promise<InvoiceListItem> {
    await request(`/api/v1/invoices/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: { toStatus: 'approved', reason: comment },
    })
    return this.getInvoice(id) as unknown as InvoiceListItem
  },

  async archiveInvoice(id: string, reason: string): Promise<void> {
    await request(`/api/v1/invoices/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: { toStatus: 'archived', reason },
    })
  },

  async addInvoiceTag(invoiceId: string, tag: string): Promise<InvoiceListItem> {
    await request(`/api/v1/invoices/${encodeURIComponent(invoiceId)}/tags`, {
      method: 'POST',
      body: { tag },
    })
    return this.getInvoice(invoiceId) as unknown as InvoiceListItem
  },

  async removeInvoiceTag(invoiceId: string, tag: string): Promise<InvoiceListItem> {
    await request(`/api/v1/invoices/${encodeURIComponent(invoiceId)}/tags/${encodeURIComponent(tag)}`, {
      method: 'DELETE',
    })
    return this.getInvoice(invoiceId) as unknown as InvoiceListItem
  },

  async listAllTags(): Promise<string[]> {
    return request<string[]>('/api/v1/invoices/all-tags/list')
  },

  async getInvoiceStats(companyId: string): Promise<{
    total: number
    bySource: Record<string, number>
    byWorkflowStatus: Record<string, number>
    lastNavSyncAt: string | null
  }> {
    return request(`/api/v1/invoices/stats${qs({ companyId })}`)
  },

  // ── AI: per-invoice elemzés és insights ────────────────────────────────
  async aiStatus(): Promise<{ enabled: boolean }> {
    return request('/api/v1/ai/status')
  },
  async aiAnalyzeInvoice(invoiceId: string): Promise<{ ok: true; saved: Array<{ kind: string; id: string }> }> {
    return request(`/api/v1/ai/invoice/${encodeURIComponent(invoiceId)}/analyze`, { method: 'POST' })
  },
  async aiListInvoiceSuggestions(invoiceId: string): Promise<{ suggestions: AiSuggestion[] }> {
    return request(`/api/v1/ai/invoice/${encodeURIComponent(invoiceId)}/suggestions`)
  },
  async aiAcceptSuggestion(suggestionId: string): Promise<{ ok: true }> {
    return request(`/api/v1/ai/suggestion/${encodeURIComponent(suggestionId)}/accept`, { method: 'POST' })
  },
  async aiRejectSuggestion(suggestionId: string): Promise<{ ok: true }> {
    return request(`/api/v1/ai/suggestion/${encodeURIComponent(suggestionId)}/reject`, { method: 'POST' })
  },
  async aiGenerateMonthlySummary(year: number, month: number, companyId: string): Promise<AiMonthlySummary> {
    return request(`/api/v1/ai/insights/monthly-summary/${year}/${month}${qs({ companyId })}`, { method: 'POST' })
  },
  async aiGetMonthlySummary(year: number, month: number, companyId: string): Promise<{ result: AiSuggestion | null }> {
    return request(`/api/v1/ai/insights/monthly-summary/${year}/${month}${qs({ companyId })}`)
  },
  async aiGenerateCashflow(companyId: string): Promise<AiCashflow> {
    return request(`/api/v1/ai/insights/cashflow${qs({ companyId })}`, { method: 'POST' })
  },
  async aiGetCashflow(companyId: string): Promise<{ result: AiSuggestion | null }> {
    return request(`/api/v1/ai/insights/cashflow${qs({ companyId })}`)
  },
  async aiGenerateAccountantPrep(year: number, month: number, companyId: string): Promise<AiAccountantPrep> {
    return request(`/api/v1/ai/insights/accountant-prep/${year}/${month}${qs({ companyId })}`, { method: 'POST' })
  },
  async aiGetAccountantPrep(year: number, month: number, companyId: string): Promise<{ result: AiSuggestion | null }> {
    return request(`/api/v1/ai/insights/accountant-prep/${year}/${month}${qs({ companyId })}`)
  },

  async uploadInvoice(payload: UploadInvoicePayload): Promise<UploadInvoiceResponse> {
    const fd = new FormData()
    fd.append('file', payload.file)
    fd.append('companyId', payload.companyId)
    if (payload.currency) fd.append('currency', payload.currency)
    if (payload.paidBy) fd.append('paidBy', payload.paidBy)
    if (payload.category) fd.append('category', payload.category)
    if (payload.projectCode) fd.append('projectCode', payload.projectCode)
    if (payload.notes) fd.append('notes', payload.notes)
    return request<UploadInvoiceResponse>('/api/v1/invoices/upload', { method: 'POST', formData: fd })
  },

  async listDiscrepancies(type?: DiscrepancyType): Promise<DiscrepancyIssue[]> {
    return request<DiscrepancyIssue[]>(`/api/v1/discrepancies${qs({ type })}`)
  },

  // A kért időszakot MINDIG a paraméterből vesszük. Korábban itt `new Date()` volt,
  // vagyis a havi zárás mindig az aktuális hónapot kérdezte, akármit választott a
  // user — júniust választva a júliusi számok jöttek, júniusnak címkézve. Havi
  // záráshoz ez végzetes, mert szinte mindig az előző hónapot zárjuk.
  async getVatMonthlySummary(companyId: string, year: number, month: number): Promise<VatMonthlySummary> {
    const co = companyId || activeCompanyHeader() || ''
    return request<VatMonthlySummary>(
      `/api/v1/vat-summary/${encodeURIComponent(co)}/${year}/${month}`,
    )
  },

  async getMonthlyExport(companyId: string, year: number, month: number): Promise<AccountantExport> {
    const co = companyId || activeCompanyHeader() || ''
    return request<AccountantExport>(
      `/api/v1/exports/${encodeURIComponent(co)}/${year}/${month}`,
    )
  },

  // Az export-workflow (jóváhagyás / elutasítás / küldés) backend-oldalon MÉG NEM
  // LÉTEZIK: nincs `approvals` tábla és nincs `/exports/*/send` végpont.
  //
  // Korábban ez a három metódus egyszerűen visszaolvasta és visszaadta ugyanazt az
  // export objektumot, amitől a UI SIKERT jelzett — a "Küldés a könyvelőnek" gomb
  // megnyugtató visszajelzést adott, miközben semmit nem küldött el senkinek. Ez
  // rosszabb, mint egy hibaüzenet: a user azt hiszi, elment az anyag.
  //
  // Amíg a backend nincs kész, inkább látható hibát dobunk.
  async approveExport(_id: string, _notes?: string): Promise<AccountantExport> {
    throw new Error(NOT_IMPLEMENTED_EXPORT_WORKFLOW)
  },

  async rejectExport(_id: string, _reason: string): Promise<AccountantExport> {
    throw new Error(NOT_IMPLEMENTED_EXPORT_WORKFLOW)
  },

  async sendExport(_id: string, _to: string): Promise<AccountantExport> {
    throw new Error(NOT_IMPLEMENTED_EXPORT_WORKFLOW)
  },

  async listJobs(): Promise<JobRun[]> {
    return request<JobRun[]>('/api/v1/jobs')
  },

  async listNotifications(unreadOnly = false): Promise<Notification[]> {
    return request<Notification[]>(`/api/v1/notifications${qs({ unreadOnly })}`)
  },

  async markNotificationRead(id: string): Promise<void> {
    await request(`/api/v1/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' })
  },

  async markAllNotificationsRead(): Promise<void> {
    await request('/api/v1/notifications/read-all', { method: 'POST' })
  },

  async listInvoiceComments(invoiceId: string): Promise<InvoiceComment[]> {
    return request<InvoiceComment[]>(`/api/v1/invoices/${encodeURIComponent(invoiceId)}/comments`)
  },

  async addInvoiceComment(
    invoiceId: string,
    body: string,
    _author: User,
    _authorRole: Role | null,
  ): Promise<InvoiceComment> {
    return request<InvoiceComment>(`/api/v1/invoices/${encodeURIComponent(invoiceId)}/comments`, {
      method: 'POST',
      body: { body },
    })
  },

  async listInbox(): Promise<EmailInboxItem[]> {
    return request<EmailInboxItem[]>('/api/v1/inbox')
  },

  async getNavCredentials(companyId: string): Promise<NavCredentials | null> {
    try {
      return await request<NavCredentials>(`/api/v1/nav-credentials/${encodeURIComponent(companyId)}`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null
      throw err
    }
  },

  async updateNavCredentials(payload: UpdateNavCredentialsPayload): Promise<NavCredentials> {
    return request<NavCredentials>(`/api/v1/nav-credentials/${encodeURIComponent(payload.companyId)}`, {
      method: 'PUT',
      body: payload,
    })
  },

  async testNavConnection(companyId: string): Promise<NavCredentials> {
    return request<NavCredentials>(`/api/v1/nav-credentials/${encodeURIComponent(companyId)}/test`, {
      method: 'POST',
    })
  },

  async getEmailAccount(): Promise<EmailAccountSettings> {
    return request<EmailAccountSettings>('/api/v1/email/incoming')
  },

  async updateEmailAccount(payload: UpdateEmailAccountPayload): Promise<EmailAccountSettings> {
    return request<EmailAccountSettings>('/api/v1/email/incoming', { method: 'PUT', body: payload })
  },

  async testEmailConnection(): Promise<EmailAccountSettings> {
    return request<EmailAccountSettings>('/api/v1/email/incoming/test', { method: 'POST' })
  },

  async listImapFolders(): Promise<ImapFoldersResult> {
    return request<ImapFoldersResult>('/api/v1/email/incoming/folders', { method: 'POST' })
  },

  async getOutgoingEmail(): Promise<OutgoingEmailSettings> {
    return request<OutgoingEmailSettings>('/api/v1/email/outgoing')
  },

  async updateOutgoingEmail(payload: UpdateOutgoingEmailPayload): Promise<OutgoingEmailSettings> {
    return request<OutgoingEmailSettings>('/api/v1/email/outgoing', { method: 'PUT', body: payload })
  },

  async testOutgoingEmail(): Promise<OutgoingEmailSettings> {
    return request<OutgoingEmailSettings>('/api/v1/email/outgoing/test', { method: 'POST' })
  },

  async getCompanySettings(companyId: string): Promise<CompanySettings | null> {
    try {
      return await request<CompanySettings>(`/api/v1/settings/company/${encodeURIComponent(companyId)}`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null
      throw err
    }
  },

  async updateCompanySettings(
    companyId: string,
    payload: UpdateCompanySettingsPayload,
  ): Promise<CompanySettings> {
    return request<CompanySettings>(`/api/v1/settings/company/${encodeURIComponent(companyId)}`, {
      method: 'PUT',
      body: payload,
    })
  },

  async triggerEmailSync(): Promise<JobResponse> {
    return request<JobResponse>('/api/v1/jobs/email-sync', { method: 'POST' })
  },

  async triggerNavSync(range?: { from: string; to: string }): Promise<JobResponse> {
    return request<JobResponse>('/api/v1/jobs/nav-sync', { method: 'POST', body: range })
  },

  // --- V1.2 új endpointok ---

  async recordPayment(
    invoiceId: string,
    payload: RecordPaymentPayload,
  ): Promise<RecordPaymentResponse> {
    return request<RecordPaymentResponse>(`/api/v1/invoices/${invoiceId}/payment`, {
      method: 'POST',
      body: payload,
    })
  },

  async downloadFromLink(payload: {
    companyId: string
    url: string
  }): Promise<LinkDownloadResponse> {
    return request<LinkDownloadResponse>('/api/v1/invoices/from-link', {
      method: 'POST',
      body: payload,
    })
  },

  async extractInvoice(invoiceId: string): Promise<ExtractResponse> {
    return request<ExtractResponse>(`/api/v1/invoices/${invoiceId}/extract`, {
      method: 'POST',
    })
  },

  async getSzamlazz(companyId: string): Promise<SzamlazzCredentialsStatus> {
    return request<SzamlazzCredentialsStatus>(`/api/v1/szamlazz/${companyId}`)
  },

  async updateSzamlazz(
    companyId: string,
    payload: UpdateSzamlazzPayload,
  ): Promise<SzamlazzCredentialsStatus> {
    return request<SzamlazzCredentialsStatus>(`/api/v1/szamlazz/${companyId}`, {
      method: 'PUT',
      body: payload,
    })
  },

  async testSzamlazz(companyId: string): Promise<{ ok: boolean; detail: string }> {
    return request<{ ok: boolean; detail: string }>(`/api/v1/szamlazz/${companyId}/test`, {
      method: 'POST',
    })
  },

  async syncSzamlazz(companyId: string): Promise<JobResponse> {
    return request<JobResponse>(`/api/v1/szamlazz/${companyId}/sync`, {
      method: 'POST',
    })
  },

  async updateCostAllocation(
    invoiceId: string,
    payload: CostAllocationPayload,
  ): Promise<{ ok: true; splits: InvoiceSplit[] }> {
    return request<{ ok: true; splits: InvoiceSplit[] }>(
      `/api/v1/invoices/${encodeURIComponent(invoiceId)}/cost-allocation`,
      { method: 'PATCH', body: payload },
    )
  },

  async getBreakdown(companyId: string, year: number, month: number): Promise<BreakdownResponse> {
    return request<BreakdownResponse>(`/api/v1/breakdown/${companyId}/${year}/${month}`)
  },

  async getBreakdownTrend(companyId: string, months: number): Promise<BreakdownTrendResponse> {
    return request<BreakdownTrendResponse>(`/api/v1/breakdown/${companyId}/trend?months=${months}`)
  },

  async bulkInvoiceAction(payload: {
    ids: string[]
    action: 'addTag' | 'removeTag' | 'setProjectCode' | 'setCategory' | 'archive' | 'approve' | 'delete'
    value?: string | null
  }): Promise<{ ok: number; fail: number; results: Array<{ id: string; ok: boolean; detail?: string }> }> {
    return request(`/api/v1/invoices/bulk`, { method: 'POST', body: payload })
  },

  async deleteInvoice(id: string): Promise<{ ok: true }> {
    return request(`/api/v1/invoices/${encodeURIComponent(id)}`, { method: 'DELETE' })
  },

  async getCronStatus(): Promise<{ jobs: CronJobStatus[] }> {
    return request<{ jobs: CronJobStatus[] }>(`/api/v1/cron/status`)
  },

  async runCronJob(type: string): Promise<{ ok: true; summary: string }> {
    return request<{ ok: true; summary: string }>(`/api/v1/cron/run/${encodeURIComponent(type)}`, {
      method: 'POST',
    })
  },

  async getCronRuns(limit = 30): Promise<{ runs: CronRun[] }> {
    return request<{ runs: CronRun[] }>(`/api/v1/cron/runs?limit=${limit}`)
  },

  async getHealthIntegrations(): Promise<HealthIntegrationsResponse> {
    return request<HealthIntegrationsResponse>(`/api/v1/health/integrations`)
  },

  async listBankTransactions(companyId: string, unmatched = false): Promise<{ items: BankTransaction[] }> {
    return request<{ items: BankTransaction[] }>(
      `/api/v1/banking/transactions?companyId=${companyId}${unmatched ? '&unmatched=1' : ''}`,
    )
  },

  async importBankCsv(companyId: string, file: File): Promise<BankingImportResponse> {
    const fd = new FormData()
    fd.append('companyId', companyId)
    fd.append('file', file)
    const token = localStorage.getItem('adaptivo.szamla.token')
    const r = await fetch(`/api/v1/banking/import`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    })
    if (!r.ok) {
      const err = await r.text()
      throw new Error(`Banking import: ${r.status} ${err.slice(0, 200)}`)
    }
    return (await r.json()) as BankingImportResponse
  },

  /**
   * OTP (és más magyar bank) PDF kivonat feltöltés.
   * Visszaad: BankingImportResponse + opcionális `metadata` (kivonat szám, nyitó/záró egyenleg)
   * + `bankName` (otp / kh / mkb / erste / unicredit / cib / mbh / raiffeisen / unknown).
   */
  async importBankPdf(
    companyId: string,
    file: File,
  ): Promise<BankingImportResponse & {
    bankName?: string
    metadata?: {
      kivonatSzam: string | null
      szamlaszam: string | null
      idoszak: string | null
      nyitoEgyenleg: number | null
      zaroEgyenleg: number | null
      terhelesekOsszesen: number | null
      jovairasokOsszesen: number | null
    }
    diagnostics?: { textSample: string; lineCount: number } | null
  }> {
    const fd = new FormData()
    fd.append('companyId', companyId)
    fd.append('file', file)
    const token = localStorage.getItem('adaptivo.szamla.token')
    const r = await fetch(`/api/v1/banking/import-pdf`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    })
    if (!r.ok) {
      const err = await r.text()
      throw new Error(`Banking PDF import: ${r.status} ${err.slice(0, 300)}`)
    }
    return await r.json()
  },

  async manualMatchBankTransaction(transactionId: string, invoiceId: string): Promise<{ ok: true }> {
    return request(`/api/v1/banking/match`, { method: 'POST', body: { transactionId, invoiceId } })
  },

  async unmatchBankTransaction(transactionId: string): Promise<{ ok: true }> {
    return request(`/api/v1/banking/match/${encodeURIComponent(transactionId)}`, { method: 'DELETE' })
  },

  async deleteBankTransaction(transactionId: string): Promise<{ ok: true; deleted: number; revertedInvoices: number }> {
    return request(`/api/v1/banking/transactions/${encodeURIComponent(transactionId)}`, { method: 'DELETE' })
  },

  async deleteBankTransactions(transactionIds: string[]): Promise<{ ok: true; deleted: number; revertedInvoices: number }> {
    return request(`/api/v1/banking/transactions/delete`, { method: 'POST', body: { transactionIds } })
  },

  async rematchBankTransactions(): Promise<{ ok: true; scanned: number; matched: number }> {
    return request(`/api/v1/banking/rematch`, { method: 'POST' })
  },
}

export type FetchApi = typeof fetchApi

/** Igaz, ha a build-be be lett állítva a `VITE_API_BASE_URL` env (akár üres stringre is). */
export const isRealBackend = RAW_BASE !== undefined

/**
 * Hibaüzenet a még meg nem valósított havi-export workflow-ra. A UI ezt jeleníti meg
 * a jóváhagyás / elutasítás / küldés gombokon, hogy senki ne higgye elküldöttnek a
 * csomagot. A tényleges átadás addig kézzel történik (lásd a havi könyvelői lista).
 */
export const NOT_IMPLEMENTED_EXPORT_WORKFLOW =
  'A havi csomag jóváhagyása és küldése még nincs kész a backendben — a csomagot egyelőre kézzel kell átadni a könyvelőnek. (Nem küldtünk el semmit.)'
